import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CardSetTransformationService, CoreService, DataService, DialogResult, DialogService, FormNavigationService, SpinnerService, VerticalNavigationSettings } from 'dspx-core';
import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ErrorService } from '../../services/error.service';
import { AppDataService } from '../../services/app-data.service';
import { FormDataService, HideableGrid } from '../../services/form-data.service';
import { QuestionPath, ResponseParserService } from '../../services/response-parser.service';
import { HtmlHelperService } from '../../services/html-helper.service';
import { JsonSerialisationService } from '../../services/json-serialisation.service';
import { TypeaheadQuestionModel } from '../../interfaces/typeahead-question-model.interface';
import { EntraIdUser } from '../../interfaces/entra-id-user.interface';
import { GbhFormSaveRequest } from '../../interfaces/gbh-form-save-request.interface';
import { API_ENDPOINT_KEYS, API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { APP_DATA_KEYS, FORM_ACTIONS, HIDEABLE_GRIDS, NAV_TAB_NUMBERS, OPTION_SET_CODES, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS, SECTION_CODES, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { take } from 'rxjs';

@Component({
  selector: 'app-gbh-form',
  templateUrl: './gbh-form.component.html',
  styleUrl: './gbh-form.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class GbhFormComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy {

  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;
  verticalNavigationSettings: VerticalNavigationSettings = { enabled: true, sticky: true, pageLevel: true };
  id = '';
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getGbhFormConfigWithEmptyResponse;
  templateId: number = TEMPLATES.gbhForm;
  versionNo: number = VERSIONS.latest;
  activeDialog: string;
  editing = false;
  isViewRoute = false;

  private versionHistoryHideableGrid: HideableGrid;
  private approver: EntraIdUser;

  constructor(
    protected router: Router,
    protected appDataService: AppDataService,
    protected coreService: CoreService,
    protected formDataService: FormDataService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    private route: ActivatedRoute,
    private errorService: ErrorService,
    private dataService: DataService,
    private spinnerService: SpinnerService,
    private responseParserService: ResponseParserService,
    private jsonSerialisationService: JsonSerialisationService,
    private formNavigationService: FormNavigationService ) {
    super(router, appDataService, coreService, formDataService, htmlHelperService, dialogService);
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  ngOnInit(): void {
    this.registerSubscription(
      this.route.paramMap.subscribe((paramMap: ParamMap) => {
        const id = paramMap.get('id');
        if (id) {
          this.id = id;
          this.isViewRoute = this.router.url === `/${ROUTE_CONSTANTS.gbhForm.viewPrefix}/${id}`;
          this.formServiceEndpoint = this.isViewRoute
            ? API_ENDPOINT_KEYS.getGbhFormConfigAndResponseReadOnly
            : API_ENDPOINT_KEYS.getGbhFormConfigAndResponse;
          this.editing = true;
        }
        else {
          this.resetDirtyFlagAfterLoading();
        }
      })
    );

    this.formNavigationService.onNavigation().subscribe(event => {
      setTimeout(() => {
        this.overrideHtmlTitleSetByDspxFramework();
      })
    });

    this.coreService.conditionFunctions['isEditing'] = () => this.editing;
    this.coreService.conditionFunctions['isReadOnly'] = () => true;
    this.coreService.conditionFunctions['showOthersLabel'] = () => this.showOthersLabel();

    this.coreService.applicationFunctions['canSave'] = () => this.isFormEditable;
    this.coreService.applicationFunctions['canDelete'] = () => this.isViewRoute;
    this.coreService.applicationFunctions['downloadCopy'] = () => this.downloadCopy();
    this.coreService.applicationFunctions['delete'] = () => this.delete(null);
    this.coreService.applicationFunctions['canSwitchToEdit'] = () => this.isViewRoute;
    this.coreService.applicationFunctions['switchToEdit'] = () => this.switchToEdit(null);

    this.versionHistoryHideableGrid = this.initialiseHideableGrid(HIDEABLE_GRIDS.gbhForm.versionHistory);
  }

  formLoaded(event: any): void {
    if (event.retryAsReadOnly) {
      this.navigate(['/', ROUTE_CONSTANTS.gbhForm.viewPrefix, this.id], true);
      return;
    }

    this.isFormEditable = !!event?.isFormEditable;

    if (this.baseForm && this.baseForm.model) {
      this.appDataService.setAppData(APP_DATA_KEYS.previousModel, JSON.parse(JSON.stringify(this.baseForm.model)));
    }

    this.versionHistoryHideableGrid.toggle(HIDEABLE_GRIDS.gbhForm.versionHistory.showOnFirstLoad);

    this.ensurePageActionButtonsAreEnabledOnFormLoad(READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS.gbhForm);
    this.handlePostFormLoadActions(event);
  }

  modelChanged(event: any): void {
    this.handleSingleCheckBoxChanges();
    this.handleApproverChange();
  }

  switchToEdit(event: any): void {
    this.navigate(['/', ROUTE_CONSTANTS.gbhForm.prefix, this.id], true);
  }

  delete(event: any): void {
    this.registerSubscription(
      this.dialogService
        .showConfirm(POP_UP_CODES.gbhForm.deleteConfirmation)
        .pipe(take(1))
        .subscribe(data => {
          switch (data as DialogResult) {
            case DialogResult.ACCEPTED: {
              this.spinnerService.showSpinner();
              this.registerSubscription(
                this.appDataService.delete(`${API_ENDPOINTS.gbhForm.delete}/${this.id}`)
                  .pipe(take(1), this.errorService.errorHandler())
                  .subscribe(data => {
                    this.spinnerService.hideSpinner();
                    const isSuccess = !SERVER_ERROR_POP_UP_CODES.deleteGbhForm.includes(data.message);
                    this.activeDialog = isSuccess ? POP_UP_CODES.gbhForm.deleteSuccessful : data.message;
                    this.dialogService.showDialog(this.activeDialog);
                    if (isSuccess) {
                      this.navigateBackToDashboard();
                    }
                  })
              );
              break;
            }
            case DialogResult.REJECTED: {
              break;
            }
          }
        })
    );
  }

  save(event: any) {

    const saveData = (request: GbhFormSaveRequest) => {
      this.saveData(request);
    }

    const saveDataRequest: GbhFormSaveRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.id,
      response: event.model,
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: event.isSubmit
    };

    if (!event.isFormValid && event.isSubmit) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showDialog(POP_UP_CODES.common.validationError);
      }
      else {
        saveData(saveDataRequest);
      }
    }
    else {
      saveData(saveDataRequest);
    }
  }

  cancel(event: any) {
    this.navigateBackToDashboard();
  }

  dialogSubmitted(event: any) {

    const activeDialog = this.activeDialog;
    delete this.activeDialog;

    switch (activeDialog) {
      case POP_UP_CODES.common.saveSuccessful: {
        this.navigateBackToDashboard(true);
        break;
      }
      case POP_UP_CODES.common.submitSuccessful: {
        if (event?.downloadCopy) {
          this.downloadCopy();
        }
        this.navigateBackToDashboard(true);
        break;
      }
    }
  }

  actionButtonClicked(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.gbhForm.versionHistory: {
        switch (event.event.type) {
          case FORM_ACTIONS.gbhForm.downloadVersion: {
            this.downloadVersion(event.event.data.id);
            break;
          }
        }
        break;
      }
    }
  }

  private navigateBackToDashboard(ignoreUnsavedChanges = false): void {
    this.navigate(['/', ROUTE_CONSTANTS.gbhForm.list], ignoreUnsavedChanges);
  }

  private saveData(request: GbhFormSaveRequest) {

    const saveDataFunction = (request: GbhFormSaveRequest) => {
      this.spinnerService.showSpinner();
      const saveEndpoint = API_ENDPOINTS.gbhForm.save;
      this.registerSubscription(
        this.dataService.saveData(saveEndpoint, request)
          .pipe(take(1), this.errorService.errorHandler())
          .subscribe(data => {
            this.spinnerService.hideSpinner();
            if (SERVER_ERROR_POP_UP_CODES.saveGbhForm.includes(data.message)) {
              this.activeDialog = data.message;
            }
            else {
              this.id = data.id;
              this.activeDialog = request.isSubmit
                ? POP_UP_CODES.common.submitSuccessful
                : POP_UP_CODES.common.saveSuccessful;
            }
            this.dialogService.showDialog(this.activeDialog);
          })
      );
    };

    saveDataFunction(request);
  }

  private downloadCopy(): void {
    const downloadCopyEndpoint = `${API_ENDPOINTS.gbhForm.downloadCopy}/${this.id}`;
    this.appDataService.generatePdf(downloadCopyEndpoint);
  }

  private downloadVersion(versionId: string): void {
    const downloadVersionEndpoint = `${API_ENDPOINTS.gbhForm.downloadVersion}/${this.id}/${versionId}`
    this.appDataService.generatePdf(downloadVersionEndpoint);
  }

  private handleSingleCheckBoxChanges(): void {
    const consentQuestionPath: QuestionPath = [
      NAV_TAB_NUMBERS.gbhForm.responseToTheOffer,
      PAGE_NUMBERS.gbhForm.responseToTheOffer,
      SECTION_CODES.gbhForm.responseDetailsSection,
      QUESTION_CODES.gbhForm.consent
    ];
    this.responseParserService.handleSingleCheckBoxQuestionChanges(
      () => this.baseForm.model,
      model => this.baseForm.model = model,
      [consentQuestionPath]);
  }

  private handleApproverChange(): void {
    const approver = this.getApprover();
    if (this.approver?.email !== approver?.email) {
      this.approver = approver;
      this.setApproverRole(approver?.role);
      this.reloadModel();
    }
  }

  private getApprover(): EntraIdUser {
    const approverQuestionModel: TypeaheadQuestionModel<string> = this.responseParserService.findQuestionModel(
      this.baseForm.model,
      PAGE_NUMBERS.gbhForm.yourDetails,
      NAV_TAB_NUMBERS.gbhForm.yourDetails,
      null,
      SECTION_CODES.gbhForm.yourDetailsSection,
      null,
      QUESTION_CODES.gbhForm.yourApprover);
    const approver = this.jsonSerialisationService.tryDeserialise<EntraIdUser>(approverQuestionModel?.code);
    return approver;
  }

  private setApproverRole(approveRole: string): void {
    this.responseParserService.replaceQuestionModel(
      this.baseForm.model,
      NAV_TAB_NUMBERS.gbhForm.yourDetails,
      PAGE_NUMBERS.gbhForm.yourDetails,
      null,
      SECTION_CODES.gbhForm.yourDetailsSection,
      null,
      QUESTION_CODES.gbhForm.approverRole,
      approveRole);
  }

  private showOthersLabel(): boolean {
    if (this.getOfficialItemQuestionModel() !== OPTION_SET_CODES.gbhForm.yesNo.no) {
      return false;
    }
    if (this.getDecisionQuestionModel() !== OPTION_SET_CODES.gbhForm.decision.accepted) {
      return false;
    }
    const relationshipQuestionModel = this.getRelationshipQuestionModel();
    return relationshipQuestionModel === OPTION_SET_CODES.gbhForm.relationship.communityOrganisation ||
      relationshipQuestionModel === OPTION_SET_CODES.gbhForm.relationship.publicSectorBody ||
      relationshipQuestionModel === OPTION_SET_CODES.gbhForm.relationship.eventOrganiser ||
      relationshipQuestionModel === OPTION_SET_CODES.gbhForm.relationship.other ||
      relationshipQuestionModel === OPTION_SET_CODES.gbhForm.relationship.healthService ||
      relationshipQuestionModel === OPTION_SET_CODES.gbhForm.relationship.university;
  }

  private getDecisionQuestionModel(): string {
    return this.responseParserService.findQuestionModel(
      this.baseForm?.model,
      NAV_TAB_NUMBERS.gbhForm.responseToTheOffer,
      PAGE_NUMBERS.gbhForm.responseToTheOffer,
      null,
      SECTION_CODES.gbhForm.responseDetailsSection,
      null,
      QUESTION_CODES.gbhForm.decision);
  }

  private getRelationshipQuestionModel(): string {
    return this.responseParserService.findQuestionModel(
      this.baseForm?.model,
      NAV_TAB_NUMBERS.gbhForm.theOffer,
      PAGE_NUMBERS.gbhForm.theOffer,
      null,
      SECTION_CODES.gbhForm.offerDetailsSection,
      null,
      QUESTION_CODES.gbhForm.offerorRelationship);
  }

  private getOfficialItemQuestionModel(): string {
    return this.responseParserService.findQuestionModel(
      this.baseForm?.model,
      NAV_TAB_NUMBERS.gbhForm.theOffer,
      PAGE_NUMBERS.gbhForm.theOffer,
      null,
      SECTION_CODES.gbhForm.offerDetailsSection,
      null,
      QUESTION_CODES.gbhForm.officialItem);
  }
}
